<template>
  <div class="performance-container">
    <!-- 顶部搜索栏 -->
    <div class="search-bar">
      <div class="date-picker" @click="showDatePicker = true">
        <span>{{ formatDate(startDate) }} - {{ formatDate(endDate) }}</span>
        <img :src="getAssetsFile('home/date@2x.png')" alt="" class="w-[16px] h-[16px] ml-[5px]">
      </div>
      <div class="id-search-box" @click="openTypeSelect" style="cursor:pointer;">
        <span style="color: #5FB589;">{{ typeOptions[selectedType].label }}</span>
        <svg class="arrow" width="18" height="18" viewBox="0 0 24 24" style="margin-left:auto;">
          <path fill="#8CAFA3" d="M7 10l5 5 5-5z" />
        </svg>
      </div>
    </div>

    <!-- 表格 -->
    <div class="table-wrap">
      <div class="table-header">
        <div class="th">{{ $t('commission.times') }}</div>
        <div class="th">{{ $t('commission.types') }}</div>
        <div class="th">{{ $t('commission.promoterLevel') }}</div>
        <!-- <div class="th">Número de Pessoas</div> -->
        <div class="th">{{ $t('commission.commission') }}</div>
      </div>
      <template v-if="tableData.length">
        <div v-for="(row, idx) in tableData" :key="row.id" :class="['table-row', { zebra: idx % 2 === 1 }]">
          <div class="td">{{ formatDateTime(row.startTime) }}</div>
          <div class="td">slots</div>
          <div class="td">{{ row.promoterLevel }}</div>
          <!-- <div class="td">{{ row.performance }}</div> -->
          <div class="td">{{ row.amount }}</div>
        </div>
      </template>
      <template v-else>
        <div class="empty-wrap">
          <div class="flex justify-center items-center">
            <img :src="getAssetsFile('home/No_record@2x.png')" alt=""  class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
          </div>
          <div class="empty-text">{{ $t('commission.noRecords') }}</div>
        </div>
      </template>
    </div>

    <!-- 日期选择弹窗 -->
    <div v-if="showDatePicker" class="date-picker-modal" @click.self="showDatePicker = false">
      <div class="date-picker-panel">
        <!-- 年月选择 -->
        <div class="panel-header">
          <span class="month-year" @click="showMonthSelect = true">
            {{ getMonthName(viewMonth) }} {{ viewYear }}
            <svg width="16" height="16" viewBox="0 0 24 24" style="vertical-align: middle;">
              <path fill="#5FB589" d="M7 10l5 5 5-5z" />
            </svg>
          </span>
          <div class="panel-arrows">
            <svg @click="prevMonth" :class="{ disabled: isPrevDisabled }" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#5FB589" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            <svg @click="nextMonth" :class="{ disabled: isNextDisabled }" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#5FB589" d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
            </svg>
          </div>
        </div>
        <!-- 月份选择 -->
        <div v-if="showMonthSelect" class="month-select">
          <div class="month-select-header" @click="showMonthSelect = false">
            <svg width="16" height="16" viewBox="0 0 24 24" style="vertical-align: middle;transform:rotate(90deg)">
              <path fill="#5FB589" d="M7 10l5 5 5-5z" />
            </svg>
            <span style="margin-left: 8px;">{{ viewYear }}</span>
          </div>
          <div class="month-list">
            <div v-for="(m, idx) in monthNames" :key="m"
              :class="['month-item', { active: idx === viewMonth, disabled: isFutureMonth(viewYear, idx) }]"
              @click="selectMonth(idx)">
              {{ getMonthName(idx) }}
            </div>
          </div>
        </div>
        <!-- 日历 -->
        <div v-else class="calendar">
          <div class="weekdays">
            <span v-for="d in weekNames" :key="d">{{ getWeekdayName(d) }}</span>
          </div>
          <div class="days">
            <span v-for="n in firstDayOfWeek" :key="'empty' + n" class="empty"></span>
            <span v-for="d in daysInMonth" :key="d" :class="[
              'day',
              { today: isToday(viewYear, viewMonth, d) },
              { selected: isTempSelected(viewYear, viewMonth, d) },
              { disabled: isFutureDate(viewYear, viewMonth, d) }
            ]" @click="selectDate(d)">{{ d }}</span>
          </div>
        </div>
        <div class="panel-footer">
          <button class="cancel-btn" @click="showDatePicker = false">{{ $t('commission.cancel') }}</button>
          <button class="ok-btn" @click="confirmDate">{{ $t('commission.done') }}</button>
        </div>
      </div>
    </div>

    <!-- 类型选择弹窗 -->
    <div v-if="showTypeSelect" class="type-select-modal" @click.self="showTypeSelect = false" align-items="center">
      <div class="type-select-panel">
        <div class="type-list">
          <div v-for="(item, idx) in typeOptions" :key="item.value" :class="['type-item', { active: idx === tempType }]"
            @click="tempType = idx">
            <span class="radio">
              <span :class="['circle', { checked: idx === tempType }]"></span>
            </span>
            <span class="label">{{ item.label }}</span>
          </div>
        </div>
        <div class="type-select-footer">
          <button class="cancel-btn" @click="showTypeSelect = false">{{ $t('commission.cancelBtn') }}</button>
          <button class="ok-btn" @click="confirmType">{{ $t('commission.okBtn') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onActivated, watch } from 'vue'
import { getPromoterRecordPage } from '@/api/game'
import { useI18n } from 'vue-i18n'
import { formatDateTime, getStartOfDay, getEndOfDay } from '@/utils/date'

const { t } = useI18n()

const today = new Date()
// 修改为支持时间范围
const startDate = ref(new Date())
const endDate = ref(new Date())
const showDatePicker = ref(false)
const showMonthSelect = ref(false)
const viewYear = ref(today.getFullYear())
const viewMonth = ref(today.getMonth())
const tempDay = ref(today.getDate())
const searchId = ref('')
const tableData = ref([]) // 这里填表格数据，空数组时显示占位
import dayjs from "dayjs";

function resetToToday() {
  const now = new Date()
  startDate.value = new Date(now)
  endDate.value = new Date(now)
  viewYear.value = now.getFullYear()
  viewMonth.value = now.getMonth()
  tempDay.value = now.getDate()
}

// 生成时间范围数组 - time[0]是开始时间，time[1]是结束时间
function generateTimeRange() {
  const start = getStartOfDay(startDate.value)
  const end = getEndOfDay(endDate.value)
  return [start, end]
}

// 获取数据函数
function fetchData() {
  const startTime = getStartOfDay(startDate.value)
  const endTime = getEndOfDay(endDate.value)

  const params = {
    pageNo: 1,
    pageSize: 10,
    total: 0,
  }

  getPromoterRecordPage(params).then(res => {
    tableData.value = res.data
  })
}

onMounted(() => {
  resetToToday()
  fetchData()
})

// 如果用了 keep-alive，页面每次激活也重置
onActivated(() => {
  resetToToday()
  fetchData()
})

// 月份名称数组（使用翻译）
const monthNames = computed(() => [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
])

// 星期名称数组（使用翻译）
const weekNames = computed(() => ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'])

// 获取月份名称
function getMonthName(monthIndex: number) {
  return t(`commission.months.${monthNames.value[monthIndex]}`)
}

// 获取星期名称
function getWeekdayName(weekday: string) {
  return t(`commission.weekdays.${weekday}`)
}

const daysInMonth = computed(() => {
  return new Date(viewYear.value, viewMonth.value + 1, 0).getDate()
})
const firstDayOfWeek = computed(() => {
  return new Date(viewYear.value, viewMonth.value, 1).getDay()
})

function formatDate(date: Date) {
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

function isToday(y: number, m: number, d: number) {
  return y === today.getFullYear() && m === today.getMonth() && d === today.getDate()
}
function isSelected(y: number, m: number, d: number) {
  return y === selectedDate.value.getFullYear() && m === selectedDate.value.getMonth() && d === selectedDate.value.getDate()
}
function isFutureDate(y: number, m: number, d: number) {
  if (y > today.getFullYear()) return true
  if (y === today.getFullYear() && m > today.getMonth()) return true
  if (y === today.getFullYear() && m === today.getMonth() && d > today.getDate()) return true
  return false
}
function isFutureMonth(y: number, m: number) {
  if (y > today.getFullYear()) return true
  if (y === today.getFullYear() && m > today.getMonth()) return true
  return false
}

function prevMonth() {
  if (viewMonth.value === 0) {
    viewYear.value--
    viewMonth.value = 11
  } else {
    viewMonth.value--
  }
  // 不能切到未来
  if (viewYear.value > today.getFullYear() || (viewYear.value === today.getFullYear() && viewMonth.value > today.getMonth())) {
    viewYear.value = today.getFullYear()
    viewMonth.value = today.getMonth()
  }
}
function nextMonth() {
  if (viewYear.value === today.getFullYear() && viewMonth.value === today.getMonth()) return
  if (viewMonth.value === 11) {
    viewYear.value++
    viewMonth.value = 0
  } else {
    viewMonth.value++
  }
  // 不能切到未来
  if (viewYear.value > today.getFullYear() || (viewYear.value === today.getFullYear() && viewMonth.value > today.getMonth())) {
    viewYear.value = today.getFullYear()
    viewMonth.value = today.getMonth()
  }
}
const isPrevDisabled = computed(() => false)
const isNextDisabled = computed(() => viewYear.value === today.getFullYear() && viewMonth.value === today.getMonth())

function selectDate(d: number) {
  if (isFutureDate(viewYear.value, viewMonth.value, d)) return
  tempDay.value = d
}
function confirmDate() {
  startDate.value = new Date(viewYear.value, viewMonth.value, tempDay.value)
  endDate.value = new Date(viewYear.value, viewMonth.value, tempDay.value)
  showDatePicker.value = false
  fetchData() // 确认日期后重新获取数据
}
function selectMonth(m: number) {
  if (isFutureMonth(viewYear.value, m)) return
  viewMonth.value = m
  showMonthSelect.value = false
  // 选完月后，天数要校验
  if (viewYear.value === today.getFullYear() && viewMonth.value === today.getMonth() && tempDay.value > today.getDate()) {
    tempDay.value = today.getDate()
  }
}
function onSearch() {
  // 搜索逻辑
}

function isTempSelected(y: number, m: number, d: number) {
  return y === viewYear.value && m === viewMonth.value && d === tempDay.value
}

// 游戏类型选项（使用翻译）
const typeOptions = computed(() => [
  { label: t('commission.gameTypes.slot'), value: 'slot' }
])

const selectedType = ref(0)
const tempType = ref(0)
const showTypeSelect = ref(false)

watch(showTypeSelect, (val) => {
  if (val) tempType.value = selectedType.value
})

function confirmType() {
  selectedType.value = tempType.value
  showTypeSelect.value = false
  fetchData() // 确认类型后重新获取数据
}

function openTypeSelect() {
  tempType.value = selectedType.value
  showTypeSelect.value = true
}
</script>

<style scoped>
.performance-container {
  background: #004D37;
  min-height: 100vh;
  padding: 16px 0 0 0;
}

.search-bar {
  display: flex;
  align-items: center;
  background: #00563A;
  border-radius: 8px 8px 0 0;
  padding: 8px 12px;
  margin: 0 0px;
  gap: 8px;
}

.date-picker {
  display: flex;
  align-items: center;
  background: #004D37;
  border-radius: 6px;
  padding: 6px 5px;
  color: #5FB589;
  font-size: 12px;
  cursor: pointer;
  min-width: 100px;
}

.date-picker .icon {
  margin-left: 6px;
}

.id-search-box {
  display: flex;
  align-items: center;
  background: #004D37;
  border-radius: 10px;
  padding: 0 12px;
  flex: 1;
  height: 28px;
  font-size: 12px;
}

.id-input {
  background: transparent;
  border: none;
  outline: none;
  color: #5FB589;
  font-size: 12px;
  padding: 0;
}

.id-input::placeholder {
  color: #5FB589;
  opacity: 0.7;
}

.search-icon {
  width: 11px;
  height: 11px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.search-icon:hover {
  opacity: 1;
}

.table-wrap {
  margin: 0px;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  background: #176343;
  color: #fff;
  font-size: 11px;
  padding: 10px 0 8px 0;
}

.th {
  flex: 1;
  text-align: center;
}

.table-row {
  display: flex;
  background: #18553a;
  color: #b6e2d3;
  font-size: 13px;
  padding: 14px 0;
  align-items: center;
  transition: background 0.2s;
}

.table-row.zebra {
  background: #176343;
}

.td {
  flex: 1;
  text-align: center;
}

.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 115px 0 0 0;
}

.empty-img {
  width: 90px;
  margin-top: 40px;
}

.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;

}

/* 日期选择弹窗 */
.date-picker-modal {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.12);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.date-picker-panel {
  background: #066147;
  border-radius: 8px;
  margin-top: 40px;
  min-width: 300px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.12);
  padding: 0 0 10px 0;
  position: relative;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 0 18px;
}

.month-year {
  color: #5FB589;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.panel-arrows svg {
  margin-left: 10px;
  cursor: pointer;
  vertical-align: middle;
}

.panel-arrows svg.disabled {
  opacity: 0.3;
  pointer-events: none;
}

.month-select {
  padding: 0 0 0 0;
}

.month-select-header {
  color: #5FB589;
  font-size: 14px;
  /* 字体缩小 */
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  width: 100%;
  padding: 6px 0;
  /* padding缩小 */
  justify-content: center;
}

.month-list {
  display: flex;
  flex-direction: column;
  max-height: 160px;
  /* 高度缩小 */
  overflow-y: auto;
  width: 100%;
  padding: 0;
}

.month-item {
  width: 100%;
  text-align: center;
  padding: 6px 0;
  /* 行高缩小 */
  color: #b6e2d3;
  font-size: 13px;
  /* 字体缩小 */
  cursor: pointer;
  border-radius: 0;
  transition: background 0.15s;
  background: transparent;
}

.month-item.active {
  color: #fff;
  font-weight: bold;

}

.month-item.disabled {
  color: #5FB589;
  opacity: 0.4;
  pointer-events: none;
}

.calendar {
  padding: 0 18px;
}

.weekdays {
  display: flex;
  justify-content: space-between;
  color: #5FB589;
  font-size: 13px;
  margin: 10px 0 0 0;
}

.days {
  display: flex;
  flex-wrap: wrap;
  margin-top: 6px;
}

.empty {
  width: 14.2%;
  height: 32px;
}

.day {
  width: 14.2%;
  height: 32px;
  line-height: 32px;
  text-align: center;
  border-radius: 50%;
  color: #b6e2d3;
  font-size: 15px;
  margin-bottom: 2px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.day.today {
  /* border: 1.5px solid #1ec98b; */
}

.day.selected {
  background: #1ec98b;
  color: #fff;
  font-weight: bold;
}

.day.disabled {
  color: #5FB589;
  opacity: 0.3;
  pointer-events: none;
}

.panel-footer {
  display: flex;
  justify-content: space-between;
  padding: 18px 18px 0 18px;
  gap: 10px;
}

.cancel-btn,
.ok-btn {
  flex: 1;
  background: none;
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  padding: 10px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

/* 类型选择弹窗 */
.type-select-modal {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.32);
  /* 更深遮罩 */
  z-index: 1000;
  display: flex;
  align-items: center;
  /* 居中 */
  justify-content: center;
}

.type-select-panel {
  background: #066147;
  border-radius: 8px;
  margin-top: 40px;
  min-width: 300px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.12);
  padding: 0 0 10px 0;
  position: relative;
}

.type-list {
  padding: 0 18px;
}

.type-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  cursor: pointer;
  /* transition: background 0.15s; */
}

.radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #1ec98b;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  box-sizing: border-box;
  flex-shrink: 0;
  background: transparent;
}

.circle {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.15s;
}

.circle.checked {
  background: #1ec98b;
}

.label {
  font-size: 14px;
  color: #b6e2d3;
}

.type-select-footer {
  display: flex;
  justify-content: space-between;
  padding: 0 18px 0 18px;
  gap: 10px;
}

.type-select-footer .cancel-btn,
.type-select-footer .ok-btn {
  flex: 1;
  background: none;
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  padding: 10px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}
</style>
